import { EnergyCostType } from '../../../calculators/costs/cost-calculator'
import { VersionCostCalculator } from '../../../calculators/costs/VersionCostCalculator'
import { PurchasedEnergyType } from '../../../generated/graphql'
import {
  Cost,
  EnergyCostRow,
  GroupedCost,
  GroupedEnergyCost,
} from './cost-table-types'

type EnergyCost = {
  energyType: PurchasedEnergyType | EnergyCostType
  value: number
  versionId: string
  multiplier: number | null
}

type CalculatedVersionData = {
  id: string
  containers: {
    id: string
    energyCosts: EnergyCost[]
  }[]
}

export const mapVersionCostCalculatorsForGrouping = (
  versionCalculators: VersionCostCalculator[],
): CalculatedVersionData[] =>
  versionCalculators.map((version) => ({
    id: version.getId(),
    containers: version.getContainers().map((container) => ({
      id: container.getId(),
      energyCosts: container
        .getEnergyCostsAndProfit()
        .map((calculatedCost) => ({
          energyType: calculatedCost.energyType,
          value:
            calculatedCost.energyType === EnergyCostType.ElectricityProfit
              ? calculatedCost.baseValue * -1
              : calculatedCost.baseValue,
          versionId: version.getId(),
          multiplier: container.getMultiplier(),
        })),
    })),
  }))

export const groupEnergyCostsToRows = (
  energyCosts: EnergyCost[],
): EnergyCostRow[] =>
  energyCosts.reduce((rows, cost) => {
    const translationKey = `energyCostType.${cost.energyType}`
    const existingRow = rows.find((c) => c.translationKey === translationKey)
    const newCost = {
      id: '',
      versionId: cost.versionId,
      value: cost.value,
      multiplier: cost.multiplier,
    }
    if (existingRow) {
      existingRow.costs.push(newCost)
    } else {
      const newRow = {
        translationKey,
        costs: [newCost],
      }
      rows.push(newRow)
    }
    return rows
  }, [] as EnergyCostRow[])

export const groupCostRows = (costRows: EnergyCostRow[]): EnergyCostRow[] =>
  costRows.reduce((rows, costRow) => {
    const existing = rows.find(
      (c) => c.translationKey === costRow.translationKey,
    )
    if (existing) {
      existing.costs = [...existing.costs, ...costRow.costs]
    } else {
      rows.push(costRow)
    }
    return rows
  }, [] as EnergyCostRow[])

type VersionForGrouping = {
  id: string
  name: string
}

export const groupEnergyCosts = <T extends VersionForGrouping>(
  containerIdsToContainerNames: Record<string, string>,
  versionCostData: CalculatedVersionData[],
  selectedVersions: T[],
): GroupedEnergyCost[] => {
  const versions = selectedVersions.map(({ id, name }) => ({
    id,
    name,
  }))

  const allContainers = versionCostData.flatMap(
    (version: CalculatedVersionData) => version.containers,
  )

  return allContainers.reduce((groupedCosts, container) => {
    const containerName = containerIdsToContainerNames[container.id]
    const costRows = groupEnergyCostsToRows(container.energyCosts)
    const existingContainer = groupedCosts.find((c) => c.name === containerName)

    if (existingContainer) {
      const updatedCostRows = groupCostRows([
        ...existingContainer.costRows,
        ...costRows,
      ])
      existingContainer.costRows = updatedCostRows
    } else {
      groupedCosts.push({
        name: containerName,
        versions,
        costRows,
      })
    }
    return groupedCosts
  }, [] as GroupedEnergyCost[])
}

export const getTotalEnergyCostsSum = (
  versionCalculator: VersionCostCalculator,
  isContainerSpecific: boolean,
  containerId?: string,
): number | null => {
  const containerNotFound = isContainerSpecific && !containerId
  return containerNotFound
    ? null
    : versionCalculator.getCumulativeEnergySum(containerId)
}

export const doEnergyCostsHaveValue = (costs: Cost[]): boolean =>
  costs.some((cost) => Math.round(cost.value) !== 0)

export const doesEnergyGroupedCostHaveValueRows = (
  groupedCost: GroupedCost,
): boolean => {
  const allCosts = groupedCost.costRows.flatMap((costRow) => costRow.costs)
  return doEnergyCostsHaveValue(allCosts)
}
